/**
 * fleet_dock.ts — the STATION DOCK window (P in space, near a station):
 * Shipyard (buy hulls) · Hangar (activate/rename) · Loadout (equip the
 * comp_/droid_ playerItems into the active ship's ten hardpoints).
 *
 * Pure mirror UI (the client-mirrors law): every button is a reducer call
 * and the panel re-renders from the replicated player_ship / ship_component /
 * pilot_state / player_item / wallet rows on a 1 s tick — it never trusts
 * its own click side-effects.
 */
import { atlas } from '../state.js';
import { CHASSIS, DEFAULT_CHASSIS, SLOTS, SLOT_LABEL, slotOfItem, shipStats, type FleetSlot } from './fleet.js';
import { FIT_BUDGET, fitCost, sysTierOf, marketEpoch, stationDrift, marketUnitPrice, HOLD_UNITS_PER_CARGO } from '@shared/space_industry.js';
import { applyPriceMod, depthMult } from '@shared/space_economy.js';
import { sfx } from './sfx.js';

type Tab = 'yard' | 'hangar' | 'loadout' | 'duty' | 'exchange' | 'orders' | 'haulage' | 'kills' | 'wars' | 'skills' | 'abyss' | 'industry' | 'hold';

/** Client mirror of server FLEET_DUTY / FLEET_ARC (display only — the server
 *  is the authority on rewards + gates). */
const DUTY_KINDS: Array<{ kind: string; label: string; brief: string; rewardSov: number; requiresRank?: number; requiresTrust?: number }> = [
  { kind: 'patrol', label: 'Patrol', brief: '3 kills at a home-cluster den', rewardSov: 60 },
  { kind: 'sweep', label: 'Sweep', brief: '5 kills at a belt den', rewardSov: 150 },
  { kind: 'strike', label: 'Strike', brief: '3 kills at a fortress — mints a battle token', rewardSov: 300 },
  // Standings-gated agents (the server re-checks every gate).
  { kind: 'vanguard', label: 'Vanguard', brief: '6 kills across the belts', rewardSov: 320, requiresRank: 2 },
  { kind: 'blockade', label: 'Blockade', brief: '5 fortress kills', rewardSov: 700, requiresRank: 3 },
  { kind: 'crucible', label: 'Crucible', brief: '8 fortress kills — the elite board', rewardSov: 1400, requiresRank: 4, requiresTrust: 60 },
];

/** Regional exchange display mirror (server computes the real prices). */
const TRADE_GOODS: Array<{ key: string; label: string; baseValue: number; tier: 'raw' | 'intermediate' | 'staple' | 'luxury' }> = [
  { key: 'grain', label: 'Grain', baseValue: 3, tier: 'raw' },
  { key: 'fish', label: 'Fish', baseValue: 4, tier: 'raw' },
  { key: 'ore', label: 'Ore', baseValue: 5, tier: 'raw' },
  { key: 'timber', label: 'Timber', baseValue: 4, tier: 'raw' },
  { key: 'fiber', label: 'Fiber', baseValue: 4, tier: 'raw' },
  { key: 'hide', label: 'Hide', baseValue: 5, tier: 'raw' },
  { key: 'herbs', label: 'Herbs', baseValue: 6, tier: 'raw' },
  { key: 'stone', label: 'Stone', baseValue: 3, tier: 'raw' },
  { key: 'luxury', label: 'Luxuries', baseValue: 14, tier: 'luxury' },
];
const ARC_TITLES: Record<string, string[]> = {
  aurelian: ['Concord Oath', 'Keeper of Lanes', 'Shield of the Belt', 'Void Warden', 'Concord Paragon'],
  borean: ['Compact Pact', 'Icefield Runner', 'Gale of the Rim', 'Bulwark of Borea', 'Aurora Sovereign'],
};
/** Versona mission-givers: the civ's duty officer (flavor names, client-only). */
const FACTION_OFFICER: Record<string, string> = {
  aurelian: 'Warden-Captain Sel Amara, Aurelian Concord',
  borean: 'Iron-Pilot Veyra Osk, Borean Compact',
  '': 'The station duty officer',
};

const BTN = 'background:#16324e;border:1px solid #2c527e;border-radius:6px;color:#cfe0f5;padding:3px 10px;cursor:pointer;font:11px ui-monospace,monospace';

export class FleetDock {
  private root: HTMLDivElement | null = null;
  private body: HTMLDivElement | null = null;
  private pilotEl: HTMLDivElement | null = null;
  private tab: Tab = 'yard';
  private timer = 0;
  private status = '';
  private readonly onClose: () => void;
  private readonly stationCtx: () => { systemId: string; stationKey: string };
  private readonly esc = (e: KeyboardEvent): void => {
    if (e.code === 'Escape' || e.code === 'KeyP') {
      const t = e.target;
      if (t instanceof Element && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      e.stopPropagation();
      this.close();
    }
  };

  constructor(onClose: () => void, stationCtx?: () => { systemId: string; stationKey: string }) {
    this.onClose = onClose;
    this.stationCtx = stationCtx ?? (() => ({ systemId: 'sys0', stationKey: 'sys0:st0' }));
  }

  open(): void {
    if (this.root) return;
    const root = document.createElement('div');
    root.id = 'fleet-dock';
    root.style.cssText =
      'position:fixed;inset:0;z-index:39;display:flex;align-items:center;justify-content:center;' +
      'background:rgba(2,5,12,.82);backdrop-filter:blur(3px);font:12px system-ui;color:#cfe0f5;pointer-events:auto';
    const panel = document.createElement('div');
    panel.style.cssText =
      'background:rgba(8,13,22,.94);border:1px solid #24405e;border-radius:12px;padding:14px;' +
      'width:640px;max-height:78vh;display:flex;flex-direction:column;gap:8px';
    const title = document.createElement('div');
    title.textContent = 'STATION DOCK';
    title.style.cssText = 'font:700 11px ui-monospace,monospace;color:#6f8cae;letter-spacing:1.4px';
    const pilot = document.createElement('div');
    pilot.style.cssText = 'font:11px ui-monospace,monospace;color:#9fb8d8';
    const tabs = document.createElement('div');
    tabs.style.cssText = 'display:flex;gap:6px';
    for (const [key, label] of [['yard', 'Shipyard'], ['hangar', 'Hangar'], ['loadout', 'Loadout'], ['duty', 'Duty'], ['exchange', 'Exchange'], ['orders', 'Orders'], ['haulage', 'Haulage'], ['kills', 'Kills'], ['wars', 'Wars'], ['skills', 'Skills'], ['abyss', 'Abyss'], ['industry', 'Industry'], ['hold', 'Hold']] as Array<[Tab, string]>) {
      const b = document.createElement('button');
      b.dataset.tab = key;
      b.textContent = label;
      b.style.cssText = this.tabCss(key === this.tab);
      b.onclick = () => { this.tab = key; this.render(); };
      tabs.appendChild(b);
    }
    const body = document.createElement('div');
    body.style.cssText = 'overflow-y:auto;display:flex;flex-direction:column;gap:6px;min-height:220px';
    const hint = document.createElement('div');
    hint.textContent = 'P / Esc — close';
    hint.style.cssText = 'color:#7f93ad;font:10px ui-monospace,monospace;text-align:center';
    panel.append(title, pilot, tabs, body, hint);
    root.appendChild(panel);
    document.body.appendChild(root);
    this.root = root;
    this.body = body;
    this.pilotEl = pilot;
    window.addEventListener('keydown', this.esc, true);
    this.timer = window.setInterval(() => this.render(), 1000);
    this.render();
  }

  close(): void {
    if (!this.root) return;
    window.removeEventListener('keydown', this.esc, true);
    window.clearInterval(this.timer);
    this.root.remove();
    this.root = null;
    this.body = null;
    this.onClose();
  }

  private tabCss(active: boolean): string {
    return `background:${active ? '#1d4468' : '#10233a'};border:1px solid #2c527e;border-radius:6px;` +
      'color:#cfe0f5;padding:4px 14px;cursor:pointer;font:11px ui-monospace,monospace';
  }

  /** The hull a courier crate rides in (its tagged lots are at ship:<id>), or null when none is aboard any hull. */
  private crateCarrier(contractId: bigint): { name: string; active: boolean } | null {
    const conn = atlas.conn;
    if (!conn?.identity) return null;
    try {
      const db = conn.db as unknown as { cargoLot: { iter(): Iterable<{ owner: { isEqual(o: unknown): boolean }; locKind: string; locId: string; tag: string }> }; playerShip: { shipId: { find(id: bigint): { name: string; active: boolean } | undefined } } };
      for (const l of db.cargoLot.iter()) {
        if (l.tag !== `c:${contractId}` || l.locKind !== 'ship' || !l.owner.isEqual(conn.identity)) continue;
        const ship = db.playerShip.shipId.find(BigInt(l.locId.replace(/^ship:/, '')));
        if (ship) return { name: ship.name, active: ship.active };
      }
    } catch { /* pre-publish */ }
    return null;
  }
  /** How many courier crates (distinct contracts) a hull carries. */
  private cratesAboard(shipId: bigint): number {
    const conn = atlas.conn;
    if (!conn?.identity) return 0;
    const seen = new Set<string>();
    try {
      const db = conn.db as unknown as { cargoLot: { iter(): Iterable<{ owner: { isEqual(o: unknown): boolean }; locKind: string; locId: string; tag: string }> } };
      for (const l of db.cargoLot.iter()) if (l.locKind === 'ship' && l.locId === `ship:${shipId}` && l.tag.startsWith('c:') && l.owner.isEqual(conn.identity)) seen.add(l.tag);
    } catch { /* pre-publish */ }
    return seen.size;
  }
  private button(label: string, enabled: boolean, onClick: () => void | Promise<unknown>): HTMLButtonElement {
    const b = document.createElement('button');
    b.textContent = label;
    b.disabled = !enabled;
    b.style.cssText = BTN + (enabled ? '' : ';opacity:.4;cursor:default');
    b.onclick = () => {
      if (!enabled) return;
      // Reducer refusals surface in the panel — never an uncaught rejection.
      const r = onClick();
      if (r && typeof (r as Promise<unknown>).then === 'function') {
        (r as Promise<unknown>).then(
          () => { sfx('sfx_coin_purchase', { gain: 0.3, cooldownMs: 300 }); this.status = ''; this.render(); },
          (e: Error) => { sfx('sfx_error', { gain: 0.25, cooldownMs: 300 }); this.status = `⚠ ${String(e?.message ?? e)}`; this.render(); },
        );
      }
      this.render();
    };
    return b;
  }

  /** `<b>name</b> <span muted>suffix</span>` built with textContent (XSS-safe). */
  private nameSpan(name: string, suffix: string): HTMLSpanElement {
    const wrap = document.createElement('span');
    const b = document.createElement('b');
    b.textContent = name;
    const s = document.createElement('span');
    s.textContent = ` ${suffix}`;
    s.style.color = '#7f93ad';
    wrap.append(b, s);
    return wrap;
  }

  private row(css = ''): HTMLDivElement {
    const d = document.createElement('div');
    d.style.cssText = 'display:flex;align-items:center;gap:10px;background:#0d1826;border:1px solid #1c3350;' +
      'border-radius:8px;padding:7px 10px;' + css;
    return d;
  }

  private render(): void {
    const conn = atlas.conn;
    const body = this.body;
    if (!body || !conn?.identity) return;
    const me = conn.identity;

    // Mirrors (guarded — the row cache can churn mid-iter).
    let sov = 0n;
    const ships: Array<{ shipId: bigint; chassisKey: string; name: string; active: boolean }> = [];
    const comps: Array<{ compId: bigint; shipId: bigint; slot: string; itemKey: string; name: string; quality: number }> = [];
    const items: Array<{ itemId: bigint; itemKey: string; name: string; quality: number }> = [];
    let faction = '', rank = 0, xp = 0n, tokens = 0n, arcStep = 0;
    const missions: Array<{ missionId: bigint; kind: string; targetPoi: number; goal: number; progress: number; rewardSov: bigint; state: string }> = [];
    try {
      const w = conn.db.wallet.identity.find(me);
      if (w) sov = w.sov;
      for (const s of conn.db.playerShip.iter()) if (s.owner.isEqual(me)) ships.push(s);
      for (const c of conn.db.shipComponent.iter()) comps.push(c);
      for (const it of conn.db.playerItem.iter()) {
        if (it.owner.isEqual(me) && slotOfItem(it.itemKey)) items.push(it);
      }
      const p = conn.db.pilotState.identity.find(me);
      if (p) { faction = p.factionKey; rank = p.rank; xp = p.xp; tokens = p.tokens; arcStep = p.arcStep; }
      for (const m of conn.db.pilotMission.iter()) {
        if (m.identity.isEqual(me) && m.state !== 'claimed') missions.push(m);
      }
    } catch { /* mid-churn — next tick */ }
    const activeShip = ships.find((s) => s.active) ?? ships[0] ?? null;
    const shipComps = activeShip ? comps.filter((c) => c.shipId === activeShip.shipId) : [];

    if (this.pilotEl) {
      const fac = faction === 'aurelian' ? 'Aurelian Concord' : faction === 'borean' ? 'Borean Compact' : 'Unaligned';
      this.pilotEl.textContent =
        `${fac} · rank ${rank} · ${xp} xp · ${tokens} tokens · ${sov} ⓒ` +
        (activeShip ? ` · flying: ${activeShip.name}` : ' · flying: stock Lancer');
    }
    for (const b of this.root?.querySelectorAll('button[data-tab]') ?? []) {
      (b as HTMLButtonElement).style.cssText = this.tabCss((b as HTMLElement).dataset.tab === this.tab);
    }

    body.replaceChildren();
    if (this.status) {
      const s = document.createElement('div');
      s.textContent = this.status;
      s.style.cssText = 'color:#ffd27e;font:11px ui-monospace,monospace';
      body.appendChild(s);
    }

    if (this.tab === 'yard') {
      for (const spec of Object.values(CHASSIS)) {
        const owned = ships.some((s) => s.chassisKey === spec.key);
        const r = this.row();
        const info = document.createElement('div');
        info.style.cssText = 'flex:1';
        info.append(
          this.nameSpan(spec.name, `— ${spec.role}`),
          Object.assign(document.createElement('div'), {
            textContent: `spd ${spec.maxSpeed} · hull ${spec.hp} · shield ${spec.shield} · cargo ${spec.cargo}`,
          }),
        );
        (info.lastChild as HTMLElement).style.cssText = 'color:#9fb8d8;font:10px ui-monospace,monospace';
        const price = document.createElement('div');
        price.style.cssText = 'font:11px ui-monospace,monospace;color:#cfe0f5;white-space:nowrap';
        price.textContent = spec.costSov === 0 ? 'stock' : `${spec.costSov} ⓒ · rank ${spec.rank}`;
        r.append(info, price);
        if (spec.costSov > 0) {
          const can = !owned && sov >= BigInt(spec.costSov) && rank >= spec.rank;
          r.appendChild(this.button(owned ? 'Owned' : 'Buy', can, () => {
            this.status = `Buying ${spec.name}…`;
            return conn.reducers.buyShip({ chassisKey: spec.key });
          }));
        } else if (owned) {
          r.appendChild(this.button('Owned', false, () => { /* stock */ }));
        }
        body.appendChild(r);
      }
    } else if (this.tab === 'hangar') {
      if (!ships.length) {
        const d = document.createElement('div');
        d.textContent = 'Your hangar is empty — launch once and the stock Lancer is issued as a real hull (fit it in Loadout).';
        d.style.cssText = 'color:#7f93ad';
        body.appendChild(d);
      }
      for (const s of ships) {
        const spec = CHASSIS[s.chassisKey] ?? CHASSIS[DEFAULT_CHASSIS];
        const r = this.row(s.active ? 'border-color:#3a6d9e' : '');
        const info = document.createElement('div');
        info.style.cssText = 'flex:1';
        // s.name is PLAYER-SET (renameShip) — never innerHTML replicated strings.
        const crates = this.cratesAboard(s.shipId);
        info.appendChild(this.nameSpan(s.name, `— ${spec.name}${s.active ? ' · ACTIVE' : ''}${crates ? ` · carries ${crates} courier crate${crates > 1 ? 's' : ''}` : ''}`));
        r.appendChild(info);
        if (!s.active) r.appendChild(this.button('Make active', true, () => conn.reducers.setActiveShip({ shipId: s.shipId })));
        r.appendChild(this.button('Rename', true, () => {
          const name = window.prompt('Ship name', s.name);
          if (name?.trim()) return conn.reducers.renameShip({ shipId: s.shipId, name: name.trim() });
        }));
        r.appendChild(this.button('Scrap', true, () => {
          if (window.confirm(`Break up ${s.name}? Components return to your inventory.`)) {
            return conn.reducers.scrapShip({ shipId: s.shipId });
          }
        }));
        // Hull insurance: 15 % premium, 40 % payout on destruction, 7-day term.
        try {
          const pol = (conn.db as unknown as { shipInsurance: { shipId: { find(id: bigint): { expiresAt: { microsSinceUnixEpoch: bigint }; payoutSov: bigint } | null | undefined } } }).shipInsurance.shipId.find(s.shipId);
          const standing = pol && Number(pol.expiresAt.microsSinceUnixEpoch / 1000n) > Date.now();
          const cost = BigInt(spec.costSov ?? 0);
          if (standing) {
            const d = document.createElement('span');
            const daysLeft = Math.max(0, Math.round((Number(pol!.expiresAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 86_400_000));
            d.textContent = `insured (pays ${pol!.payoutSov} ⓒ · ${daysLeft} d)`;
            d.style.cssText = 'color:#7bc86c;font:10px ui-monospace,monospace';
            r.appendChild(d);
          } else if (cost > 0n) {
            const premium = (cost * 1500n) / 10_000n;
            r.appendChild(this.button(`Insure (${premium} ⓒ)`, sov >= premium, () =>
              (conn.reducers as unknown as { insureShip(a: { shipId: bigint }): Promise<void> }).insureShip({ shipId: s.shipId })));
          }
        } catch { /* pre-publish server */ }
        body.appendChild(r);
      }
    } else if (this.tab === 'loadout') {
      if (!activeShip) {
        const d = document.createElement('div');
        d.textContent = 'No hull row yet — launch once and the stock Lancer is issued with its ten hardpoints.';
        d.style.cssText = 'color:#7f93ad';
        body.appendChild(d);
      } else {
        const st = shipStats(activeShip.chassisKey, shipComps);
        const sum = document.createElement('div');
        sum.style.cssText = 'font:10px ui-monospace,monospace;color:#9fb8d8';
        sum.textContent =
          `derived: spd ${Math.round(st.maxSpeed)} · hull ${st.hp} · shield ${st.shield} · cap ${st.capacitor}` +
          ` · dmg ×${st.weaponMult.toFixed(2)} · missiles ${st.missiles} · counters ${st.counters}`;
        body.appendChild(sum);
        // FITTING BUDGET: the chassis' CPU/powergrid vs the loadout draw —
        // better modules draw more; you cannot max every slot.
        const budget = FIT_BUDGET[activeShip.chassisKey] ?? { cpu: 200, pg: 200 };
        let cpuUsed = 0, pgUsed = 0;
        for (const c of shipComps) { const fc = fitCost(c.slot, c.quality); cpuUsed += fc.cpu; pgUsed += fc.pg; }
        const fitBar = (label: string, used: number, max: number): HTMLDivElement => {
          const wrap = document.createElement('div');
          wrap.style.cssText = 'display:flex;align-items:center;gap:8px;font:10px ui-monospace,monospace;color:#9fb8d8';
          const l = document.createElement('span');
          l.textContent = `${label} ${used}/${max}`;
          l.style.width = '110px';
          const track = document.createElement('div');
          track.style.cssText = 'flex:1;height:6px;background:#0b1420;border:1px solid #1c3450;border-radius:3px;overflow:hidden';
          const fill = document.createElement('div');
          const pct = Math.min(100, Math.round(used / max * 100));
          fill.style.cssText = `height:100%;width:${pct}%;background:${used > max ? '#c0392b' : pct > 85 ? '#d8a03c' : '#2c7ea0'}`;
          track.appendChild(fill);
          wrap.append(l, track);
          return wrap;
        };
        body.appendChild(fitBar('CPU', cpuUsed, budget.cpu));
        body.appendChild(fitBar('POWERGRID', pgUsed, budget.pg));
        for (const slot of SLOTS) {
          const fitted = shipComps.find((c) => c.slot === slot);
          const r = this.row();
          const label = document.createElement('div');
          label.style.cssText = 'width:120px;color:#7f93ad;font:11px ui-monospace,monospace';
          label.textContent = SLOT_LABEL[slot as FleetSlot];
          const val = document.createElement('div');
          val.style.cssText = 'flex:1';
          val.textContent = fitted ? `${fitted.name} · q${fitted.quality}` : '—';
          r.append(label, val);
          if (fitted) {
            r.appendChild(this.button('Unequip', true, () =>
              conn.reducers.unequipShipComponent({ shipId: activeShip.shipId, slot })));
          }
          body.appendChild(r);
        }
        const invTitle = document.createElement('div');
        invTitle.textContent = 'COMPONENT INVENTORY';
        invTitle.style.cssText = 'font:700 10px ui-monospace,monospace;color:#6f8cae;letter-spacing:1.2px;margin-top:4px';
        body.appendChild(invTitle);
        if (!items.length) {
          const d = document.createElement('div');
          d.textContent = 'No ship components — pirates drop them, stations will sell them.';
          d.style.cssText = 'color:#7f93ad';
          body.appendChild(d);
        }
        for (const it of items) {
          const slot = slotOfItem(it.itemKey) as string;
          let cpuFree = 0, pgFree = 0;
          for (const c of shipComps) {
            if (c.slot === slot) continue;
            const fc = fitCost(c.slot, c.quality);
            cpuFree += fc.cpu; pgFree += fc.pg;
          }
          const nc = fitCost(slot, it.quality);
          const fits = cpuFree + nc.cpu <= budget.cpu && pgFree + nc.pg <= budget.pg;
          const r = this.row();
          const info = document.createElement('div');
          info.style.cssText = 'flex:1';
          info.textContent = `${it.name} · q${it.quality} → ${SLOT_LABEL[slotOfItem(it.itemKey) as FleetSlot]} · draws ${nc.cpu} cpu / ${nc.pg} pg`;
          r.append(info, this.button(fits ? 'Equip' : 'Over budget', fits, () =>
            conn.reducers.equipShipComponent({ shipId: activeShip.shipId, itemId: it.itemId })));
          body.appendChild(r);
        }
      }
    } else if (this.tab === 'exchange') {
      // ── Regional exchange: prices drift per station; core sells basics
      // cheap, the rim pays premiums — hauling is the arbitrage.
      const { systemId, stationKey } = this.stationCtx();
      const tier = sysTierOf(systemId);
      const epoch = marketEpoch(BigInt(Date.now()) * 1000n);
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = `${stationKey} exchange · ${tier === 1 ? 'CORE prices (basics glutted)' : tier === 3 ? 'RIM prices (basics at a premium)' : 'mid-belt prices'} · marks drift every 2 h`;
      body.appendChild(head);
      // My sellable stock per commodity (family → commodity bridge mirror).
      const FAMILY_COMMODITY: Record<string, string> = {
        ferrite_ore: 'ore', noble_ore: 'luxury', crystal: 'luxury', stone: 'stone',
        reagent: 'herbs', gas: 'herbs', solvent: 'herbs',
        grain: 'grain', produce: 'grain', game: 'fish', brine_catch: 'fish',
        fiber: 'fiber', hide: 'hide', timber: 'timber', resin: 'timber',
      };
      // THE WAREHOUSE LAW (Space Wave 2): what sells HERE is the hold + your
      // warehouse at this station — never the ground bag, never a consigned crate.
      const stock: Record<string, number> = {};
      try {
        const activeId = [...conn.db.playerShip.iter()].find((s) => s.owner.isEqual(me) && s.active)?.shipId ?? null;
        for (const l of conn.db.cargoLot.iter()) {
          if (!l.owner.isEqual(me) || l.tag) continue;
          const here = (l.locKind === 'ship' && activeId !== null && l.locId === `ship:${activeId}`) || (l.locKind === 'station' && l.locId === stationKey);
          if (!here) continue;
          const c = FAMILY_COMMODITY[l.familyKey];
          if (c) stock[c] = (stock[c] ?? 0) + l.qty;
        }
      } catch { /* fine */ }
      for (const g of TRADE_GOODS) {
        const drift = stationDrift(stationKey, g.key, epoch, tier, g.tier);
        // Space Wave 3 (THE YARD LAW): a station's shortage multiplies both sides of the book.
        let mod = 1;
        try { for (const m of conn.db.spacePriceMod.iter()) if (m.stationKey === stationKey && m.commodity === g.key) { mod = m.mult; break; } } catch { /* not subscribed yet */ }
        // THE DEPTH LAW (the port wave): what this epoch already bought lifts the mark, what it sold sinks it — the server's own rule.
        let boughtU = 0, soldU = 0;
        try {
          const ddb = conn.db as unknown as { spaceMarketDepth: { iter(): Iterable<{ stationKey: string; commodity: string; epoch: bigint; boughtU: number; soldU: number }> } };
          for (const d of ddb.spaceMarketDepth.iter()) if (d.stationKey === stationKey && d.commodity === g.key && d.epoch === BigInt(epoch)) { boughtU = d.boughtU; soldU = d.soldU; break; }
        } catch { /* not subscribed yet */ }
        const buyPx = applyPriceMod(applyPriceMod(marketUnitPrice(g.baseValue, drift, 'buy'), mod), depthMult('buy', boughtU));
        const sellPx = applyPriceMod(applyPriceMod(marketUnitPrice(g.baseValue, drift, 'sell'), mod), depthMult('sell', soldU));
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${g.label} — buy ${buyPx} ⓒ · sell ${sellPx} ⓒ · hold ${stock[g.key] ?? 0}${mod > 1 ? ` · ×${mod.toFixed(2)} — the yard is short` : ''}`;
        r.append(d,
          this.button('Buy 10', sov >= buyPx * 10n, () =>
            conn.reducers.spaceMarketBuy({ stationKey, commodity: g.key, qty: 10 })),
          this.button('Sell 10', (stock[g.key] ?? 0) >= 10, () =>
            conn.reducers.spaceMarketSell({ stationKey, commodity: g.key, qty: 10 })));
        body.appendChild(r);
      }
      const note = document.createElement('div');
      note.style.cssText = 'color:#7f93ad;font:10px system-ui';
      note.textContent = 'Mined ore and salvage sell here too (their families bridge to the exchange). A courier hold full of cheap core staples pays for itself rimward.';
      body.appendChild(note);
    } else if (this.tab === 'orders') {
      // ── The ORDER BOOK: standing bids and asks per resource family. FIFO
      // fills at the resting price; 3 % broker at placement, 5 % tax per fill.
      const { stationKey } = this.stationCtx();
      const db = conn.db as unknown as { marketOrder: { iter(): Iterable<{ orderId: bigint; owner: { isEqual(o: unknown): boolean }; stationKey: string; side: string; familyKey: string; priceSov: bigint; qty: number }> } };
      const orders = (() => { try { return [...db.marketOrder.iter()].filter((o) => o.stationKey === stationKey); } catch { return []; } })();
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = `${stationKey} order book · ${orders.length} standing orders · broker 3% · sales tax 5%`;
      body.appendChild(head);
      const FAMS = ['grain', 'produce', 'ferrite_ore', 'stone', 'crystal', 'fiber', 'timber', 'gas'];
      // Placement form.
      const form = this.row();
      const sel = document.createElement('select');
      sel.style.cssText = 'background:#0d1a2a;border:1px solid #2c527e;border-radius:6px;color:#cfe0f5;padding:2px 4px;font:11px ui-monospace,monospace';
      for (const f of FAMS) { const o = document.createElement('option'); o.value = f; o.textContent = f.replace(/_/g, ' '); sel.appendChild(o); }
      const sideSel = document.createElement('select');
      sideSel.style.cssText = sel.style.cssText;
      for (const sd of ['buy', 'sell']) { const o = document.createElement('option'); o.value = sd; o.textContent = sd.toUpperCase(); sideSel.appendChild(o); }
      const px = document.createElement('input'); px.type = 'number'; px.placeholder = 'price'; px.value = '10';
      const qy = document.createElement('input'); qy.type = 'number'; qy.placeholder = 'qty'; qy.value = '10';
      for (const inp of [px, qy]) inp.style.cssText = 'width:64px;background:#0d1a2a;border:1px solid #2c527e;border-radius:6px;color:#cfe0f5;padding:2px 6px;font:11px ui-monospace,monospace';
      form.append(sel, sideSel, px, qy, this.button('Place', true, () =>
        (conn.reducers as unknown as { placeMarketOrder(a: { stationKey: string; side: string; familyKey: string; priceSov: bigint; qty: number }): Promise<void> })
          .placeMarketOrder({ stationKey, side: sideSel.value, familyKey: sel.value, priceSov: BigInt(Math.max(1, Number(px.value) | 0)), qty: Math.max(1, Number(qy.value) | 0) })));
      body.appendChild(form);
      // The book per family: best bid/ask + depth.
      for (const f of FAMS) {
        const asks = orders.filter((o) => o.familyKey === f && o.side === 'sell').sort((x, y) => (x.priceSov < y.priceSov ? -1 : 1));
        const bids = orders.filter((o) => o.familyKey === f && o.side === 'buy').sort((x, y) => (x.priceSov > y.priceSov ? -1 : 1));
        if (!asks.length && !bids.length) continue;
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
        const ask0 = asks[0] ? `ask ${asks[0].priceSov} ⓒ ×${asks.reduce((n, o) => n + o.qty, 0)}` : 'no asks';
        const bid0 = bids[0] ? `bid ${bids[0].priceSov} ⓒ ×${bids.reduce((n, o) => n + o.qty, 0)}` : 'no bids';
        d.textContent = `${f.replace(/_/g, ' ')} — ${bid0} · ${ask0}`;
        r.appendChild(d);
        body.appendChild(r);
      }
      // My standing orders.
      const mine = orders.filter((o) => o.owner.isEqual(me));
      if (mine.length) {
        const mh = document.createElement('div');
        mh.style.cssText = 'color:#6f8cae;font:700 10px ui-monospace,monospace;letter-spacing:1px;margin-top:6px';
        mh.textContent = 'MY ORDERS';
        body.appendChild(mh);
        for (const o of mine) {
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
          d.textContent = `${o.side.toUpperCase()} ${o.qty} × ${o.familyKey.replace(/_/g, ' ')} @ ${o.priceSov} ⓒ`;
          r.append(d, this.button('Cancel', true, () =>
            (conn.reducers as unknown as { cancelMarketOrder(a: { orderId: bigint }): Promise<void> }).cancelMarketOrder({ orderId: o.orderId })));
          body.appendChild(r);
        }
      }
    } else if (this.tab === 'haulage') {
      // ── COURIER CONTRACTS: collateral-backed hauling. The crate rides in
      // YOUR hold — lose the ship, lose the collateral.
      const { stationKey } = this.stationCtx();
      interface CourierRow { contractId: bigint; issuer: { isEqual(o: unknown): boolean }; courier: { isEqual(o: unknown): boolean }; originKey: string; destKey: string; cargoJson: string; rewardSov: bigint; collateralSov: bigint; state: string; expiresAt: { microsSinceUnixEpoch: bigint } }
      const db = conn.db as unknown as { courierContract: { iter(): Iterable<CourierRow> } };
      const rows = (() => { try { return [...db.courierContract.iter()]; } catch { return []; } })();
      const cargoLabel = (j: string): string => {
        try { return (JSON.parse(j) as Array<{ familyKey: string; qty: number }>).map((c) => `${c.qty}×${c.familyKey.replace(/_/g, ' ')}`).join(', '); }
        catch { return '?'; }
      };
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = `Haulage board — you dock at ${stationKey}. Deliver AT the destination station.`;
      body.appendChild(head);
      // Post a contract from here.
      const form = this.row();
      const dest = document.createElement('input'); dest.placeholder = 'dest (sys9:st0)'; dest.value = '';
      const fam = document.createElement('input'); fam.placeholder = 'family'; fam.value = 'grain';
      const q2 = document.createElement('input'); q2.type = 'number'; q2.placeholder = 'qty'; q2.value = '10';
      const rw = document.createElement('input'); rw.type = 'number'; rw.placeholder = 'reward'; rw.value = '50';
      const co = document.createElement('input'); co.type = 'number'; co.placeholder = 'collateral'; co.value = '100';
      for (const inp of [dest, fam, q2, rw, co]) inp.style.cssText = 'width:82px;background:#0d1a2a;border:1px solid #2c527e;border-radius:6px;color:#cfe0f5;padding:2px 6px;font:11px ui-monospace,monospace';
      form.append(dest, fam, q2, rw, co, this.button('Post', true, () =>
        (conn.reducers as unknown as { createCourierContract(a: { originKey: string; destKey: string; cargo: Array<{ familyKey: string; qty: number }>; rewardSov: bigint; collateralSov: bigint; durationS: number }): Promise<void> })
          .createCourierContract({ originKey: stationKey, destKey: dest.value.trim(), cargo: [{ familyKey: fam.value.trim(), qty: Math.max(1, Number(q2.value) | 0) }], rewardSov: BigInt(Math.max(1, Number(rw.value) | 0)), collateralSov: BigInt(Math.max(0, Number(co.value) | 0)), durationS: 24 * 3600 })));
      body.appendChild(form);
      for (const c of rows.filter((x) => x.state === 'offered' || x.state === 'accepted').slice(0, 20)) {
        const minsLeft = Math.max(0, Math.round((Number(c.expiresAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000));
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
        d.textContent = `${c.originKey} → ${c.destKey} · ${cargoLabel(c.cargoJson)} · pays ${c.rewardSov} ⓒ · bond ${c.collateralSov} ⓒ · ${minsLeft} min · ${c.state}`;
        r.appendChild(d);
        const R = conn.reducers as unknown as {
          acceptCourierContract(a: { contractId: bigint }): Promise<void>;
          deliverCourierContract(a: { contractId: bigint }): Promise<void>;
          cancelCourierContract(a: { contractId: bigint }): Promise<void>;
        };
        if (c.state === 'offered' && !c.issuer.isEqual(me)) r.appendChild(this.button('Accept', true, () => R.acceptCourierContract({ contractId: c.contractId })));
        if (c.state === 'offered' && c.issuer.isEqual(me)) r.appendChild(this.button('Cancel', true, () => R.cancelCourierContract({ contractId: c.contractId })));
        if (c.state === 'accepted' && c.courier.isEqual(me)) {
          // THE CUSTODY LAW: the crate is a tagged lot in ONE hull's hold — say which, and warn when it is not the active one.
          const carrier = this.crateCarrier(c.contractId);
          if (carrier) {
            const w = document.createElement('span');
            w.style.cssText = carrier.active ? 'color:#8fd0ff;font:11px ui-monospace,monospace' : 'color:#ffb86b;font:700 11px ui-monospace,monospace';
            w.textContent = carrier.active ? ` · aboard ${carrier.name} (active)` : ` · ⚠ aboard ${carrier.name}, NOT your active hull — make it active before you fly`;
            d.appendChild(w);
          }
          const atDest = c.destKey === stationKey;
          r.appendChild(this.button(atDest ? 'Deliver' : `Deliver at ${c.destKey}`, atDest, () => R.deliverCourierContract({ contractId: c.contractId })));
        }
        body.appendChild(r);
      }
      // ── PORT RUNS (the planetary port, 2026-09-17): a Commons city's ore to its civ's yard and back.
      // The crates stay the PORT's while you fly them — take a run DOWN here (docked at the yard),
      // land a run UP here; a run down lands at the port city's spaceport (H on the ground).
      {
        interface PortHaulRow { haulId: bigint; portId: bigint; direction: string; familyKey: string; qty: number; rewardSov: bigint; state: string; hauler: { isEqual(o: unknown): boolean }; stationKey: string; deadlineAt: { microsSinceUnixEpoch: bigint } }
        interface PortRow { portId: bigint; settlementId: bigint; state: string }
        const hdb = conn.db as unknown as { portHaul?: { iter(): Iterable<PortHaulRow> }; planetaryPort?: { iter(): Iterable<PortRow> } };
        const runs = (() => { try { return hdb.portHaul ? [...hdb.portHaul.iter()].filter((h) => h.state === 'offered' || h.state === 'aboard') : []; } catch { return []; } })();
        const ports = (() => { try { return hdb.planetaryPort ? [...hdb.planetaryPort.iter()] : []; } catch { return []; } })();
        const ph = document.createElement('div');
        ph.style.cssText = 'color:#6f8cae;font:700 10px ui-monospace,monospace;letter-spacing:1px;margin-top:8px';
        ph.textContent = `PORT RUNS — a town's ore to its yard and back · ${runs.length ? `${runs.length} live` : 'none on offer'}`;
        body.appendChild(ph);
        const RP = conn.reducers as unknown as { portAccept(a: { haulId: bigint }): Promise<void>; portDeliver(a: { haulId: bigint }): Promise<void> };
        for (const h of runs.slice(0, 8)) {
          const port = ports.find((p) => p.portId === h.portId);
          const minsLeft = Math.max(0, Math.round((Number(h.deadlineAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000));
          const mine = h.state === 'aboard' && h.hauler.isEqual(me);
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
          d.textContent = `${h.direction === 'up' ? 'UP' : 'DOWN'} · ${h.qty} u ${h.familyKey.replace(/_/g, ' ')} · ${h.direction === 'up' ? `city ${port?.settlementId ?? '?'} → ${h.stationKey}` : `${h.stationKey} → city ${port?.settlementId ?? '?'}`} · pays ${h.rewardSov} ⓒ · ${minsLeft} min · ${h.state}${mine ? ' (yours)' : ''}`;
          r.appendChild(d);
          const here = h.stationKey === stationKey;
          if (h.state === 'offered' && h.direction === 'down') r.appendChild(this.button(here ? 'Take the run' : `Take at ${h.stationKey}`, here, () => RP.portAccept({ haulId: h.haulId })));
          else if (h.state === 'offered' && h.direction === 'up') r.appendChild(this.button('Take it at the port city (H)', false, () => undefined));
          else if (mine && h.direction === 'up') r.appendChild(this.button(here ? 'Land the run' : `Land at ${h.stationKey}`, here, () => RP.portDeliver({ haulId: h.haulId })));
          else if (mine && h.direction === 'down') r.appendChild(this.button('Land it at the port city (H)', false, () => undefined));
          body.appendChild(r);
        }
      }
      // ── FREELANCE BOARDS: player-authored objectives, escrow-paid ──
      {
        const fh = document.createElement('div');
        fh.style.cssText = 'color:#6f8cae;font:700 10px ui-monospace,monospace;letter-spacing:1px;margin-top:8px';
        fh.textContent = 'FREELANCE BOARDS — verified kills or delivered goods, paid per unit';
        body.appendChild(fh);
        interface ProjRow { projectId: bigint; issuer: { isEqual(o: unknown): boolean }; title: string; kind: string; targetKey: string; goal: number; progress: number; rewardPerSov: bigint; state: string }
        const pdb = conn.db as unknown as { freelanceProject: { iter(): Iterable<ProjRow> } };
        const projs = (() => { try { return [...pdb.freelanceProject.iter()].filter((p2) => p2.state === 'open'); } catch { return []; } })();
        const R2 = conn.reducers as unknown as {
          createFreelanceProject(a2: { title: string; kind: string; targetKey: string; goal: number; rewardPerSov: bigint; durationS: number }): Promise<void>;
          claimFreelanceKills(a2: { projectId: bigint }): Promise<void>;
          deliverFreelanceGoods(a2: { projectId: bigint; qty: number }): Promise<void>;
          cancelFreelanceProject(a2: { projectId: bigint }): Promise<void>;
        };
        const pform = this.row();
        const pt = document.createElement('input'); pt.placeholder = 'title'; pt.value = '';
        const pk = document.createElement('select');
        for (const kk of ['kills', 'deliver']) { const o = document.createElement('option'); o.value = kk; o.textContent = kk; pk.appendChild(o); }
        const ptk = document.createElement('input'); ptk.placeholder = 'family (deliver)'; ptk.value = 'grain';
        const pg = document.createElement('input'); pg.type = 'number'; pg.placeholder = 'goal'; pg.value = '10';
        const pp = document.createElement('input'); pp.type = 'number'; pp.placeholder = 'ⓒ/unit'; pp.value = '5';
        for (const inp of [pt, ptk, pg, pp]) inp.style.cssText = 'width:80px;background:#0d1a2a;border:1px solid #2c527e;border-radius:6px;color:#cfe0f5;padding:2px 6px;font:11px ui-monospace,monospace';
        pk.style.cssText = 'background:#0d1a2a;border:1px solid #2c527e;border-radius:6px;color:#cfe0f5;padding:2px 4px;font:11px ui-monospace,monospace';
        pform.append(pt, pk, ptk, pg, pp, this.button('Post board', true, () =>
          R2.createFreelanceProject({ title: pt.value.trim() || 'Untitled work', kind: pk.value, targetKey: ptk.value.trim(), goal: Math.max(1, Number(pg.value) | 0), rewardPerSov: BigInt(Math.max(1, Number(pp.value) | 0)), durationS: 48 * 3600 })));
        body.appendChild(pform);
        for (const p2 of projs.slice(0, 12)) {
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
          d.textContent = `${p2.title} — ${p2.kind}${p2.kind === 'deliver' ? ` (${p2.targetKey.replace(/_/g, ' ')})` : p2.kind === 'escort' ? ' (fly with the run — E in flight)' : ''} · ${p2.progress}/${p2.goal} · ${p2.rewardPerSov} ⓒ${p2.kind === 'escort' ? ' at arrival' : '/unit'}`;
          r.appendChild(d);
          if (p2.issuer.isEqual(me)) r.appendChild(this.button('Cancel', true, () => R2.cancelFreelanceProject({ projectId: p2.projectId })));
          else if (p2.kind === 'kills') r.appendChild(this.button('Claim my kills', true, () => R2.claimFreelanceKills({ projectId: p2.projectId })));
          else if (p2.kind === 'deliver') r.appendChild(this.button('Deliver 10', true, () => R2.deliverFreelanceGoods({ projectId: p2.projectId, qty: 10 })));
          body.appendChild(r);
        }
      }
    } else if (this.tab === 'kills') {
      // ── KILL RECORDS: every hull loss is public history.
      interface KillRow { killId: bigint; victimName: string; chassisKey: string; shipName: string; systemKey: string; attackerKind: string; attackerRef: string; valueSov: bigint; at: { microsSinceUnixEpoch: bigint } }
      const db = conn.db as unknown as { killRecord: { iter(): Iterable<KillRow> } };
      const kills = (() => { try { return [...db.killRecord.iter()]; } catch { return []; } })()
        .sort((x, y) => (x.killId > y.killId ? -1 : 1)).slice(0, 30);
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = `The kill board — ${kills.length} recent hull losses. Losses are REAL: the fit goes down with her.`;
      body.appendChild(head);
      if (!kills.length) {
        const d = document.createElement('div');
        d.style.cssText = 'color:#7f93ad';
        d.textContent = 'No hulls lost yet. Fly dangerous.';
        body.appendChild(d);
      }
      for (const k of kills) {
        const mins = Math.round((Date.now() - Number(k.at.microsSinceUnixEpoch / 1000n)) / 60_000);
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
        d.appendChild(this.nameSpan(`${k.victimName}`, `lost ${CHASSIS[k.chassisKey]?.name ?? k.chassisKey} in ${k.systemKey} · ${k.valueSov} ⓒ · ${k.attackerKind}${k.attackerRef ? ` (${k.attackerRef})` : ''} · ${mins < 60 ? `${mins} min` : `${Math.round(mins / 60)} h`} ago`));
        r.appendChild(d);
        body.appendChild(r);
      }
      // ── THE BOUNTY OFFICE: post a sealed writ on another pilot ──────
      const bh = document.createElement('div');
      bh.style.cssText = 'margin-top:10px;color:#e8b4a0;font:11px ui-monospace,monospace';
      bh.textContent = 'THE QUIET DESK — post a sealed writ. 400 ⓒ buys a Hired Blade; 1500 a Tracker; 5000 a Revenant; 15000 a Wraith. The guild never says who paid.';
      body.appendChild(bh);
      const form = this.row();
      const nameIn = document.createElement('input');
      nameIn.placeholder = 'pilot name';
      nameIn.style.cssText = 'flex:1;background:#0a0f18;border:1px solid #2a3a52;color:#dfe8f4;font:11px ui-monospace,monospace;padding:4px 6px;border-radius:3px';
      const spendIn = document.createElement('input');
      spendIn.placeholder = 'ⓒ';
      spendIn.value = '400';
      spendIn.style.cssText = 'width:70px;background:#0a0f18;border:1px solid #2a3a52;color:#dfe8f4;font:11px ui-monospace,monospace;padding:4px 6px;border-radius:3px';
      for (const el of [nameIn, spendIn]) {
        el.addEventListener('keydown', (ev) => ev.stopPropagation());
        el.addEventListener('keyup', (ev) => ev.stopPropagation());
        el.addEventListener('keypress', (ev) => ev.stopPropagation());
      }
      form.append(nameIn, spendIn, this.button('Post the writ', true, () => {
        const spend = BigInt(Math.max(0, Math.floor(Number(spendIn.value) || 0)));
        (conn.reducers as unknown as { placeBounty(a: { targetUsername: string; spend: bigint }): Promise<void> })
          .placeBounty({ targetUsername: nameIn.value.trim(), spend })
          .then(() => { nameIn.value = ''; this.render(); })
          .catch((e: Error) => { const d = document.createElement('div'); d.style.cssText = 'color:#ff8a7a;font:10px ui-monospace,monospace'; d.textContent = String(e.message ?? e); form.appendChild(d); });
      }));
      body.appendChild(form);
      // My writs (the guild reports outcomes to the buyer alone).
      interface WritRow { bountyId: bigint; placedBy: { isEqual(o: unknown): boolean }; state: string; hunterName: string; tier: number; spend: bigint }
      const writs = (() => {
        try {
          return [...(conn.db as unknown as { bountyContract: { iter(): Iterable<WritRow> } }).bountyContract.iter()]
            .filter((w) => w.placedBy.isEqual(me));
        } catch { return []; }
      })();
      for (const w of writs.sort((x, y) => (x.bountyId > y.bountyId ? -1 : 1)).slice(0, 8)) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:10px ui-monospace,monospace;color:#9fb8d8';
        const st = w.state === 'stalking' ? 'the hunter watches' : w.state === 'attacking' ? 'the hunter has committed'
          : w.state === 'fulfilled' ? 'CLOSED — the debt was collected' : 'CLOSED — the hunter died';
        d.textContent = `writ #${w.bountyId} · ${w.hunterName} (T${w.tier}, ${w.spend} ⓒ) — ${st}`;
        r.appendChild(d);
        body.appendChild(r);
      }
    } else if (this.tab === 'wars') {
      // ── GUILD WARS: wardecs legalize sieges on the enemy's platforms ──
      interface GW { gwId: bigint; guildA: bigint; guildB: bigint; state: string; endsAt: { microsSinceUnixEpoch: bigint } }
      interface GRow { guildId: bigint; name: string; tag: string }
      interface GM { guildId: bigint; identity: { isEqual(o: unknown): boolean }; title: string }
      const gdb = conn.db as unknown as { guildWar: { iter(): Iterable<GW> }; guild: { iter(): Iterable<GRow> }; guildMember: { iter(): Iterable<GM> } };
      const R3 = conn.reducers as unknown as {
        declareGuildWar(a2: { targetGuildId: bigint }): Promise<void>;
        surrenderGuildWar(a2: { gwId: bigint }): Promise<void>;
        attackSpaceStructure(a2: { structureId: bigint }): Promise<void>;
        repairSpaceStructure(a2: { structureId: bigint }): Promise<void>;
      };
      const myM = (() => { try { return [...gdb.guildMember.iter()].find((m2) => m2.identity.isEqual(me)); } catch { return undefined; } })();
      const gname = (id: bigint): string => { try { const g = [...gdb.guild.iter()].find((x) => x.guildId === id); return g ? `${g.name} <${g.tag}>` : `guild ${id}`; } catch { return `guild ${id}`; } };
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = myM ? `Wars of ${gname(myM.guildId)} — a declaration costs 500 ⓒ and runs 7 days` : 'You fly no colors — join a guild (V) to make war.';
      body.appendChild(head);
      const wars = (() => { try { return [...gdb.guildWar.iter()].filter((w) => w.state === 'declared'); } catch { return []; } })();
      for (const w of wars.slice(0, 10)) {
        const minesIt = myM && (w.guildA === myM.guildId || w.guildB === myM.guildId);
        const r = this.row(minesIt ? 'border-color:#8a3a32' : '');
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
        const daysLeft = Math.max(0, Math.round((Number(w.endsAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 86_400_000));
        d.textContent = `⚔ ${gname(w.guildA)} vs ${gname(w.guildB)} · ${daysLeft} d left`;
        r.appendChild(d);
        if (minesIt && (myM!.title === 'leader' || myM!.title === 'warden')) {
          r.appendChild(this.button('Surrender', true, () => R3.surrenderGuildWar({ gwId: w.gwId })));
        }
        body.appendChild(r);
      }
      if (myM && (myM.title === 'leader' || myM.title === 'warden')) {
        const others = (() => { try { return [...gdb.guild.iter()].filter((g) => g.guildId !== myM.guildId); } catch { return []; } })();
        for (const g of others.slice(0, 8)) {
          const atWar = wars.some((w) => (w.guildA === myM.guildId && w.guildB === g.guildId) || (w.guildB === myM.guildId && w.guildA === g.guildId));
          if (atWar) continue;
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
          d.textContent = `${g.name} <${g.tag}>`;
          r.append(d, this.button('Declare war (500 ⓒ)', sov >= 500n, () => R3.declareGuildWar({ targetGuildId: g.guildId })));
          body.appendChild(r);
        }
      }
      // Siegeable enemy platforms + my platforms under siege.
      interface SS { structId: bigint; owner: { isEqual(o: unknown): boolean }; kind: string; systemId: string }
      interface Siege { structureId: bigint; hp: number; state: string; reinforcedUntil: { microsSinceUnixEpoch: bigint } }
      const sdb = conn.db as unknown as { spaceStructure: { iter(): Iterable<SS> }; structureSiege: { iter(): Iterable<Siege> } };
      const sieges = (() => { try { return [...sdb.structureSiege.iter()]; } catch { return []; } })();
      const sh = document.createElement('div');
      sh.style.cssText = 'color:#6f8cae;font:700 10px ui-monospace,monospace;letter-spacing:1px;margin-top:8px';
      sh.textContent = 'SIEGES — hp → 8 h reinforcement → the final battle';
      body.appendChild(sh);
      try {
        for (const st of [...sdb.spaceStructure.iter()].slice(0, 14)) {
          const siege = sieges.find((x) => x.structureId === st.structId);
          const minePlat = st.owner.isEqual(me);
          const rfLeft = siege && siege.state === 'reinforced' ? Math.max(0, Math.round((Number(siege.reinforcedUntil.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000)) : 0;
          const r = this.row(siege ? 'border-color:#8a3a32' : '');
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
          d.textContent = `${st.kind} · ${st.systemId} · ${minePlat ? 'YOURS' : 'theirs'}${siege ? ` · ${siege.state === 'reinforced' ? (rfLeft > 0 ? `REINFORCED ${rfLeft} min` : 'timer OUT — final battle') : `${siege.hp} hp`}` : ''}`;
          r.appendChild(d);
          if (!minePlat) r.appendChild(this.button('Attack', true, () => R3.attackSpaceStructure({ structureId: st.structId })));
          else if (siege) r.appendChild(this.button('Repair', true, () => R3.repairSpaceStructure({ structureId: st.structId })));
          body.appendChild(r);
        }
      } catch { /* pre-publish */ }
    } else if (this.tab === 'skills') {
      // ── THE SKILL QUEUE: one slot, trains offline, changes real numbers ──
      const SKILLS: Record<string, string> = {
        gunnery: '+4 % bolt damage / level', shields: '+5 % shield pool / level',
        navigation: '+3 % speed / level', trade: 'broker fee −0.3 % / level (server-real)',
        salvaging: 'richer wreck strips / level', command: '+2 % squadron duty pay / level',
      };
      interface SkillRow { identity: { isEqual(o: unknown): boolean }; skillKey: string; level: number }
      interface TrainRow { identity: { isEqual(o: unknown): boolean }; skillKey: string; endsAt: { microsSinceUnixEpoch: bigint } }
      const kdb = conn.db as unknown as { pilotSkill: { iter(): Iterable<SkillRow> }; pilotTraining: { iter(): Iterable<TrainRow> } };
      const R4 = conn.reducers as unknown as { trainSkill(a2: { skillKey: string }): Promise<void> };
      const mySkills = (() => { try { return [...kdb.pilotSkill.iter()].filter((sk) => sk.identity.isEqual(me)); } catch { return []; } })();
      const training = (() => { try { return [...kdb.pilotTraining.iter()].find((tr) => tr.identity.isEqual(me)); } catch { return undefined; } })();
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = training
        ? `Training ${training.skillKey} — done in ${Math.max(0, Math.round((Number(training.endsAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000))} min (offline counts)`
        : 'The training slot is idle — queue a skill. Level N takes N × 30 min, offline time included.';
      body.appendChild(head);
      for (const [key, desc] of Object.entries(SKILLS)) {
        const lvl = mySkills.find((sk) => sk.skillKey === key)?.level ?? 0;
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
        d.textContent = `${key} ${'▰'.repeat(lvl)}${'▱'.repeat(5 - lvl)} — ${desc}`;
        r.appendChild(d);
        if (lvl < 5) r.appendChild(this.button(training ? 'Slot busy' : `Train L${lvl + 1} (${(lvl + 1) * 30} min)`, !training, () => R4.trainSkill({ skillKey: key })));
        body.appendChild(r);
      }
    } else if (this.tab === 'abyss') {
      // ── ABYSSAL POCKETS: filament in, 20 minutes or the ship is LOST ──
      interface RunRow { runId: bigint; identity: { isEqual(o: unknown): boolean }; tier: number; state: string; roomsClaimed: number; expiresAt: { microsSinceUnixEpoch: bigint } }
      const adb = conn.db as unknown as { abyssalRun: { iter(): Iterable<RunRow> } };
      const R5 = conn.reducers as unknown as {
        buyAbyssalFilament(a2: { tier: number }): Promise<void>;
        enterAbyssal(a2: { tier: number }): Promise<void>;
        claimAbyssalRoom(a2: { runId: bigint }): Promise<void>;
        exitAbyssal(a2: { runId: bigint }): Promise<void>;
      };
      const open = (() => { try { return [...adb.abyssalRun.iter()].find((r2) => r2.identity.isEqual(me) && r2.state === 'open'); } catch { return undefined; } })();
      const head = document.createElement('div');
      head.style.cssText = 'color:#b88fd8;font:11px ui-monospace,monospace';
      head.textContent = open
        ? `INSIDE THE POCKET (t${open.tier}) — ${open.roomsClaimed}/3 chambers · ${Math.max(0, Math.round((Number(open.expiresAt.microsSinceUnixEpoch / 1000n) - Date.now()) / 60_000))} min before it TAKES THE SHIP`
        : 'The abyss: three chambers of pay in twenty minutes — overstay and the ship is LOST (killmail and all).';
      body.appendChild(head);
      const filaments = items.filter((it) => /^filament_t\d$/.test(it.itemKey));
      for (const tier of [1, 2, 3]) {
        const held = filaments.filter((f) => f.itemKey === `filament_t${tier}`).length;
        const cost = tier === 1 ? 80 : tier === 2 ? 200 : 450;
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
        d.textContent = `t${tier} filament — ${cost} ⓒ · held ${held} · rooms pay ${tier * 60}/${tier * 80}/${tier * 100} ⓒ + t${tier} hardware`;
        r.append(d,
          this.button('Buy', sov >= BigInt(cost), () => R5.buyAbyssalFilament({ tier })),
          this.button('Enter', held > 0 && !open, () => R5.enterAbyssal({ tier })));
        body.appendChild(r);
      }
      if (open) {
        const r = this.row('border-color:#7a4a9e');
        r.append(
          this.button('Breach the next chamber', open.roomsClaimed < 3, () => R5.claimAbyssalRoom({ runId: open.runId })),
          this.button('Take the exit', true, () => R5.exitAbyssal({ runId: open.runId })));
        body.appendChild(r);
      }
    } else if (this.tab === 'industry') {
      // ── The refinery line: raw stock in, refined stock out (35% here;
      // your own refinery beacon runs at 50%).
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = 'Station refinery — 35% line yield · your own refinery beacon runs 50% (K deploys one) · refines what is HERE: aboard or in your warehouse';
      body.appendChild(head);
      const { stationKey: hereKey } = this.stationCtx();
      const activeId2 = [...conn.db.playerShip.iter()].find((s) => s.owner.isEqual(me) && s.active)?.shipId ?? null;
      const lots: Array<{ lotId: bigint; name: string; familyKey: string; qty: number; locKind: string; tag: string }> = [];
      try {
        for (const l of conn.db.cargoLot.iter()) {
          if (!l.owner.isEqual(me)) continue;
          if ((l.locKind === 'ship' && activeId2 !== null && l.locId === `ship:${activeId2}`) || (l.locKind === 'station' && l.locId === hereKey)) lots.push(l);
        }
      } catch { /* fine */ }
      if (!lots.length) {
        const d = document.createElement('div');
        d.textContent = 'Nothing here — mine an asteroid field (hold U inside one), strip the war graves, or buy at the Exchange.';
        d.style.cssText = 'color:#7f93ad';
        body.appendChild(d);
      }
      for (const l of lots) {
        const refined = l.name.startsWith('Refined ');
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${l.name} · ${l.familyKey.replace(/_/g, ' ')} · ${l.qty} u · ${l.locKind === 'ship' ? 'aboard' : 'warehouse'}${refined ? ' · REFINED' : ''}${l.tag ? ' · consigned' : ''}`;
        r.appendChild(d);
        if (!refined && !l.tag && l.qty >= 3) {
          r.appendChild(this.button(`Refine all (${Math.floor(l.qty * 0.35)} out)`, true, () =>
            conn.reducers.spaceRefine({ lotId: l.lotId, qty: l.qty, structId: 0n })));
        }
        body.appendChild(r);
      }
    } else if (this.tab === 'hold') {
      // ── THE HOLD LAW + THE WAREHOUSE LAW (Space Wave 2): the active hull's
      // hold (cargo × 25 u) and your warehouse AT THIS STATION; Load / Unload
      // move goods between them; consigned crates stay aboard.
      const { stationKey: hereKey } = this.stationCtx();
      const active = [...conn.db.playerShip.iter()].find((s) => s.owner.isEqual(me) && s.active) ?? null;
      let cargoQ = 0;
      if (active) for (const c of conn.db.shipComponent.iter()) if (c.shipId === active.shipId && c.slot === 'cargo') cargoQ = c.quality;
      const cap = active ? ((CHASSIS[active.chassisKey]?.cargo ?? 4) + Math.round(cargoQ / 1000 * 20)) * HOLD_UNITS_PER_CARGO : 0;
      const mine = (() => { try { return [...conn.db.cargoLot.iter()].filter((l) => l.owner.isEqual(me)); } catch { return []; } })();
      const hold = active ? mine.filter((l) => l.locKind === 'ship' && l.locId === `ship:${active.shipId}`) : [];
      const here = mine.filter((l) => l.locKind === 'station' && l.locId === hereKey);
      const used = hold.reduce((n, l) => n + l.qty, 0);
      const head = document.createElement('div');
      head.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      head.textContent = `${active?.name ?? 'no hull'} — hold ${used}/${cap} u · warehouse at ${hereKey}: ${here.reduce((n, l) => n + l.qty, 0)} u`;
      body.appendChild(head);
      const bar = document.createElement('div');
      bar.style.cssText = 'height:6px;background:#10233a;border:1px solid #2c527e;border-radius:4px;overflow:hidden';
      const fill = document.createElement('div');
      fill.style.cssText = `height:100%;width:${cap ? Math.min(100, used / cap * 100) : 0}%;background:${used >= cap ? '#d06a4a' : '#4fa3e0'}`;
      bar.appendChild(fill);
      body.appendChild(bar);
      const sec = (label: string): void => {
        const h = document.createElement('div');
        h.style.cssText = 'color:#6f8cae;font:700 10px ui-monospace,monospace;letter-spacing:1px;margin-top:6px';
        h.textContent = label;
        body.appendChild(h);
      };
      sec('ABOARD');
      if (!hold.length) { const d = document.createElement('div'); d.style.cssText = 'color:#7f93ad'; d.textContent = 'The hold is empty.'; body.appendChild(d); }
      for (const l of hold) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${l.name} · ${l.qty} u${l.tag ? ' · consigned (rides to its destination)' : ''}`;
        r.appendChild(d);
        r.appendChild(this.button('Unload', !l.tag, () => conn.reducers.unloadCargo({ lotId: l.lotId, qty: l.qty })));
        body.appendChild(r);
      }
      sec('IN THE WAREHOUSE HERE');
      if (!here.length) { const d = document.createElement('div'); d.style.cssText = 'color:#7f93ad'; d.textContent = 'Nothing stored at this station.'; body.appendChild(d); }
      for (const l of here) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${l.name} · ${l.qty} u`;
        r.appendChild(d);
        r.appendChild(this.button('Load', used < cap, () => conn.reducers.loadCargo({ lotId: l.lotId, qty: l.qty })));
        body.appendChild(r);
      }
    } else {
      // ── Duty: contracts + the faction arc (versona-civ career) ──
      // Loyalty (tier-2): claimed contracts bank LP; the store trades it.
      try {
        interface LpRow { identity: { isEqual(o: unknown): boolean }; factionKey: string; lp: bigint }
        const ldb = conn.db as unknown as { pilotLoyalty: { iter(): Iterable<LpRow> } };
        const lpRow = [...ldb.pilotLoyalty.iter()].find((l) => l.identity.isEqual(me));
        const lp = lpRow?.lp ?? 0n;
        const R6 = conn.reducers as unknown as { redeemLoyalty(a2: { offerKey: string }): Promise<void> };
        const lpHead = document.createElement('div');
        lpHead.style.cssText = 'color:#d9a94e;font:11px ui-monospace,monospace;margin-bottom:2px';
        lpHead.textContent = `Loyalty: ${lp} LP${lpRow ? ` (${lpRow.factionKey})` : ''} — claimed contracts +25, war work +10`;
        body.appendChild(lpHead);
        for (const [key, cost, label] of [['crate_faction', 50n, 'Faction weapon (q700)'], ['crate_engine', 90n, 'Faction engine (q780)'], ['crate_elite', 150n, 'Elite shield emitter (q850)']] as Array<[string, bigint, string]>) {
          const r = this.row();
          const d = document.createElement('div');
          d.style.cssText = 'flex:1;font:11px ui-monospace,monospace';
          d.textContent = `${label} — ${cost} LP`;
          r.append(d, this.button('Redeem', lp >= cost, () => R6.redeemLoyalty({ offerKey: key })));
          body.appendChild(r);
        }
      } catch { /* pre-publish */ }
      const officer = document.createElement('div');
      officer.textContent = `${FACTION_OFFICER[faction] ?? FACTION_OFFICER['']} has work for a pilot.`;
      officer.style.cssText = 'color:#9fb8d8;font:11px ui-monospace,monospace';
      body.appendChild(officer);
      if (!faction) {
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = 'Swear to a civ for the +20% allegiance dividend and the career arc:';
        r.append(d,
          this.button('Aurelian Concord', true, () => conn.reducers.setPilotFaction({ factionKey: 'aurelian' })),
          this.button('Borean Compact', true, () => conn.reducers.setPilotFaction({ factionKey: 'borean' })));
        body.appendChild(r);
      }
      for (const m of missions) {
        const r = this.row(m.state === 'done' ? 'border-color:#3a6d9e' : '');
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = `${m.kind.toUpperCase()} — den poi${m.targetPoi} · ${m.progress}/${m.goal}${m.state === 'done' ? ' · COMPLETE' : ''}`;
        r.appendChild(d);
        if (m.state === 'done') {
          r.appendChild(this.button(`Claim ${m.rewardSov} ⓒ${faction ? ' +20%' : ''}`, true, () =>
            conn.reducers.claimDutyMission({ missionId: m.missionId })));
        } else {
          r.appendChild(this.button('Abandon', true, () => conn.reducers.abandonDutyMission({ missionId: m.missionId })));
        }
        body.appendChild(r);
      }
      // The agent ladder: higher boards open with rank (and, for the elite
      // tier, P7 Trustworthiness) — pay also scales +15%/rank at claim.
      let trust = 50;
      try {
        const rep = conn.db.reputation.identity.find(me);
        if (rep) trust = rep.trustworthiness;
      } catch { /* fine */ }
      for (const dk of DUTY_KINDS) {
        if (missions.some((m) => m.kind === dk.kind)) continue;
        const lockedRank = (dk.requiresRank ?? 0) > rank;
        const lockedTrust = (dk.requiresTrust ?? 0) > trust;
        const locked = lockedRank || lockedTrust;
        const r = this.row(locked ? 'opacity:.55' : '');
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        const payPct = rank * 15 + (faction ? 20 : 0);
        d.textContent = `${dk.label} — ${dk.brief} · ${dk.rewardSov} ⓒ${payPct ? ` (+${payPct}% standing pay)` : ''}`
          + (locked ? ` · LOCKED: ${lockedRank ? `rank ${dk.requiresRank}+` : ''}${lockedRank && lockedTrust ? ' · ' : ''}${lockedTrust ? `trust ${dk.requiresTrust}+` : ''}` : '');
        r.append(d, this.button('Take', !locked, () => conn.reducers.takeDutyMission({ kind: dk.kind })));
        body.appendChild(r);
      }
      if (faction) {
        const titles = ARC_TITLES[faction] ?? [];
        const arcT = document.createElement('div');
        arcT.textContent = 'CAREER ARC';
        arcT.style.cssText = 'font:700 10px ui-monospace,monospace;color:#6f8cae;letter-spacing:1.2px;margin-top:4px';
        body.appendChild(arcT);
        const r = this.row();
        const d = document.createElement('div');
        d.style.cssText = 'flex:1';
        d.textContent = arcStep >= titles.length
          ? `Arc complete — ${titles[titles.length - 1]}`
          : `Next: ${titles[arcStep]} (step ${arcStep + 1}/${titles.length})`;
        r.appendChild(d);
        if (arcStep < titles.length) {
          r.appendChild(this.button('Advance', true, () => {
            this.status = 'Advancing the arc…';
            conn.reducers.advanceFactionArc({}).then(
              () => { this.status = ''; this.render(); },
              (e: Error) => { this.status = String(e.message ?? e); this.render(); },
            );
          }));
        }
        body.appendChild(r);
      }
      const cr = this.row();
      const cd = document.createElement('div');
      cd.style.cssText = 'flex:1';
      cd.textContent = `Battle tokens: ${tokens} — 3 open a component crate (q400-800)`;
      cr.append(cd, this.button('Open crate', tokens >= 3n, () => conn.reducers.redeemFleetTokens({})));
      body.appendChild(cr);
    }
  }
}
